/**
 * Der zentrale Eingangskorb und die E-Rechnungen.
 *
 * `07` §7.4 beschreibt den Korb als **Arbeitsliste**, nicht als Postfach: Er
 * ersetzt die Quellsysteme nicht, sondern zeigt je Vorgang Herkunft, Vorschau,
 * Vorschlag mit Begründung, Fehler, Bearbeiter und Prüfstatus.
 *
 * Der Bestand deckt deshalb alle sechs Bearbeitungszustände aus `01` §1.12 ab
 * — `neu`, `in_pruefung`, `zugeordnet`, `zurueckgestellt`, `abgewiesen`,
 * `quarantaene`. Besonders die letzten beiden: Ein Korb, in dem nie etwas
 * abgewiesen wird, hat für „Ablehnen" nie eine Ansicht gebraucht, und die
 * Quarantäne aus der Schadsoftware-Prüfung (ADR-023) ist genau der Zustand, in
 * dem eine Oberfläche am ehesten zu viel preisgibt.
 */

import type { ERechnung, Eingangsvorgang } from "./modell.js";
import { AUFTRAG, DOKUMENT, EINGANG, MAIL, PERSON } from "./kennungen.js";
import { tag, zeitpunkt } from "./zeit.js";

export function eingang(bezugstag: Date): readonly Eingangsvorgang[] {
  return [
    {
      // Eine E-Rechnung, die durchgelaufen ist und auf Zuordnung wartet. Der
      // Vorschlag steht mit Begründung da und ist **noch nicht** übernommen —
      // `07` §7.4: „Automatische Vorschläge bleiben bis zur Bestätigung
      // sichtbar als Vorschlag."
      //
      // **Der Vorschlag steht nicht hier.** Er wird von `berechneVorschlag`
      // aus der Bestellnummer im Betreff gerechnet — 88214 gehört zum
      // Material von Auftrag 2490. Vorher stand er als festes Feld daneben
      // und behauptete eine Regel, die es nicht gab; das ist die Sorte
      // Attrappe, die man beim Durchklicken nicht bemerkt.
      ref: EINGANG.rechnungElektrogrosshandel,
      art: "erechnung",
      quelle: MAIL.rechnungElektrogrosshandel,
      betreff: "Rechnung 2026-114237 zu Ihrer Bestellung 88214",
      absender: "rechnung@elektrogrosshandel-nord-demo.de",
      eingegangen: zeitpunkt(bezugstag, 0, 6, 12),
      status: "neu",
      anhaenge: [DOKUMENT.rechnungElektrogrosshandel],
      vorschau: "Wallbox 11 kW, Zählerschrankmaterial, Leitung NYM-J 5x6 – 1.284,55 EUR brutto",
    },
    {
      ref: EINGANG.rechnungKabelwerk,
      art: "erechnung",
      quelle: MAIL.rechnungKabelwerk,
      betreff: "XRechnung 4471-2026",
      absender: "buchhaltung@kabelwerk-sued-demo.de",
      eingegangen: zeitpunkt(bezugstag, -1, 14, 38),
      status: "in_pruefung",
      bearbeiter: PERSON.buero,
      // **Hier schweigt die Heuristik, und das ist der Punkt.** Die Rechnung
      // nennt weder Auftrags- noch Bestellnummer; der Artikel steht nur als
      // Bezeichnung darin, und Bezeichnungen sind frei getippt. Ein Vorschlag
      // stand hier trotzdem — begründet mit einer Regel, die es nicht gab.
      // Der Normalfall im Korb ist der Vorgang, den ein Mensch zuordnet.
      anhaenge: [DOKUMENT.rechnungKabelwerk],
      vorschau: "Verlegekabel Cat.7 S/FTP, 500 m Trommel – 892,10 EUR brutto",
    },
    {
      // Der Fall, den `07` §7.16 ausdrücklich fordert: formal ungültig, aber
      // nicht still verworfen und nicht als gültig markiert. Er bleibt im
      // Korb und trägt seinen Befund sichtbar.
      ref: EINGANG.rechnungFehlerhaft,
      art: "erechnung",
      quelle: MAIL.rechnungFehlerhaft,
      betreff: "Ihre Rechnung – ZUGFeRD",
      absender: "office@leuchtenhandel-ost-demo.de",
      eingegangen: zeitpunkt(bezugstag, -1, 9, 5),
      status: "neu",
      anhaenge: [DOKUMENT.rechnungFehlerhaft],
      fehler:
        "Validierung fehlgeschlagen: Der Gesamtbetrag im eingebetteten XML weicht vom "
        + "sichtbaren PDF ab. Vorgang wurde nicht zugeordnet.",
      vorschau: "Hallenstrahler 150 W, 18 Stück – PDF nennt 4.212,00 EUR, XML nennt 4.221,00 EUR",
    },
    {
      ref: EINGANG.anfrageSchule,
      art: "email",
      quelle: MAIL.anfrageSchule,
      betreff: "Anfrage Elektroarbeiten Grundschule – Angebot erbeten",
      absender: "bauamt@schoenbrunn-demo.de",
      eingegangen: zeitpunkt(bezugstag, 0, 7, 55),
      status: "neu",
      anhaenge: [],
      vorschau:
        "Sehr geehrte Damen und Herren, für die Grundschule benötigen wir ein Angebot "
        + "über die Erneuerung der Beleuchtung in vier Klassenräumen …",
    },
    {
      // Zugeordnet und erledigt: Der Korb muss auch zeigen können, was durch
      // ist, sonst lässt sich der Unterschied zwischen „offen" und „fertig"
      // in der Oberfläche nicht beurteilen.
      ref: EINGANG.reklamationBaeckerei,
      art: "email",
      quelle: MAIL.reklamationBaeckerei,
      betreff: "Kühlung schaltet wieder ab",
      absender: "b.hufnagel@baeckerei-hufnagel-demo.de",
      eingegangen: zeitpunkt(bezugstag, -3, 5, 41),
      status: "zugeordnet",
      bearbeiter: PERSON.buero,
      zugeordneterAuftrag: AUFTRAG.kuehlung,
      anhaenge: [],
      vorschau: "Heute Nacht ist die Kühlung wieder ausgegangen, Display E9. Bitte dringend …",
    },
    {
      ref: EINGANG.lieferschein,
      art: "dokument",
      quelle: DOKUMENT.lieferschein,
      betreff: "Lieferschein 77-2026-4412",
      absender: "Scanner Büro (Einzug)",
      eingegangen: zeitpunkt(bezugstag, -1, 11, 20),
      status: "zugeordnet",
      bearbeiter: PERSON.buero,
      zugeordneterAuftrag: AUFTRAG.hallenbeleuchtung,
      anhaenge: [],
      vorschau: "18 Hallenstrahler 150 W, geliefert an Metallbau Grasser",
    },
    {
      ref: EINGANG.pruefprotokoll,
      art: "upload",
      quelle: DOKUMENT.pruefprotokoll2025,
      betreff: "Prüfprotokoll Vorjahr (vom Kunden nachgereicht)",
      absender: "r.merkle@autohaus-merkle-demo.de",
      eingegangen: zeitpunkt(bezugstag, -6, 16, 2),
      status: "zugeordnet",
      bearbeiter: PERSON.inhaber,
      zugeordneterAuftrag: AUFTRAG.hebebuehne,
      anhaenge: [],
      vorschau: "Prüfprotokoll Hebebühnen 1 und 2, Vorjahr",
    },
    {
      // Zurückgestellt mit Grund im Freitext. Der Zustand existiert, damit
      // etwas aus der Arbeitsliste verschwinden kann, ohne entschieden zu
      // sein — sonst wird jeder unklare Vorgang entweder falsch zugeordnet
      // oder bleibt für immer oben.
      ref: EINGANG.angebotsanfrage,
      art: "email",
      quelle: MAIL.angebotsanfrageMetallbau,
      betreff: "Zweite Halle – grobe Kostenschätzung?",
      absender: "info@metallbau-grasser-demo.de",
      eingegangen: zeitpunkt(bezugstag, -8, 10, 15),
      status: "zurueckgestellt",
      bearbeiter: PERSON.inhaber,
      anhaenge: [],
      vorschau: "Wir überlegen, die zweite Halle nächstes Jahr auch umzurüsten …",
    },
    {
      ref: EINGANG.wartungsvertrag,
      art: "dokument",
      quelle: DOKUMENT.wartungsvertragSeniorenheim,
      betreff: "Wartungsvertrag Rufanlage – gegengezeichnete Fassung",
      absender: "leitung@st-elisabeth-demo.de",
      eingegangen: zeitpunkt(bezugstag, -25, 9, 0),
      status: "zugeordnet",
      bearbeiter: PERSON.inhaber,
      zugeordneterAuftrag: AUFTRAG.rufanlage,
      anhaenge: [],
      vorschau: "Wartungsvertrag, Laufzeit 24 Monate, halbjährliche Prüfung",
    },
    {
      // Quarantäne nach ADR-023. Die Vorschau ist bewusst **kein Auszug des
      // Inhalts**: Was der Signaturscanner beanstandet hat, wird nicht zur
      // Ansicht gerendert, und der Anhang bleibt unerreichbar. Genau hier
      // wäre eine hilfsbereite Oberfläche gefährlich.
      ref: EINGANG.verdaechtigerAnhang,
      art: "email",
      quelle: MAIL.verdaechtig,
      betreff: "Offene Forderung – Vorgang 41822",
      absender: "mahnung@inkasso-zentrale-demo.net",
      eingegangen: zeitpunkt(bezugstag, -2, 3, 17),
      status: "quarantaene",
      anhaenge: [],
      fehler:
        "Anhang wurde bei der Eingangsprüfung beanstandet und ist gesperrt. "
        + "Keine Vorschau, kein Download. Freigabe nur durch den Administrator.",
    },
    {
      // Abgewiesen: Der Scan war unbrauchbar. Der Vorgang bleibt auffindbar,
      // damit niemand ihn ein zweites Mal bearbeitet und sich wundert.
      ref: EINGANG.scanUnleserlich,
      art: "dokument",
      quelle: DOKUMENT.pruefprotokoll2025,
      betreff: "Scan ohne Inhalt",
      absender: "Scanner Büro (Einzug)",
      eingegangen: zeitpunkt(bezugstag, -4, 8, 44),
      status: "abgewiesen",
      bearbeiter: PERSON.buero,
      anhaenge: [],
      fehler: "Doppeleinzug – Seite leer. Original erneut eingescannt unter 77-2026-4412.",
    },
  ];
}

/**
 * Die strukturierten Rechnungsdaten zu den drei E-Rechnungen im Korb.
 *
 * Alle drei Ausgänge der Validierung kommen vor. Der mittlere — gültig, aber
 * mit Anmerkungen — ist der, den ein boolesches Feld verschluckt hätte: Die
 * Rechnung ist verarbeitbar, trägt aber einen Befund, den jemand sehen sollte.
 *
 * Beträge stehen in Cent. Eine Fließkommazahl, die als `1284.5499999999999`
 * durch eine Oberfläche läuft, ist genau die Sorte Fehler, die niemandem
 * auffällt, bis ein Kunde die Rechnung nachrechnet.
 */
export function rechnungen(bezugstag: Date): readonly ERechnung[] {
  return [
    {
      ref: DOKUMENT.rechnungElektrogrosshandel,
      eingangsvorgang: EINGANG.rechnungElektrogrosshandel,
      steller: "Elektrogroßhandel Nord GmbH",
      rechnungsnummer: "2026-114237",
      datum: tag(bezugstag, -1),
      betragCent: 128455,
      waehrung: "EUR",
      profil: "zugferd-en16931",
      validierung: "gueltig",
      befunde: [],
      original: DOKUMENT.rechnungElektrogrosshandel,
      originalHash: "sha256:4f1c9a2e7b6d5083c1ae94ff20b7d3c8e5a61402f9db73cc0e18a45b2d6f7091",
    },
    {
      ref: DOKUMENT.rechnungKabelwerk,
      eingangsvorgang: EINGANG.rechnungKabelwerk,
      steller: "Kabelwerk Süd AG",
      rechnungsnummer: "4471-2026",
      datum: tag(bezugstag, -2),
      betragCent: 89210,
      waehrung: "EUR",
      profil: "xrechnung-ubl",
      validierung: "mit-anmerkungen",
      befunde: [
        "BR-DE-15: Leitweg-ID fehlt. Für einen gewerblichen Empfänger nicht zwingend, "
        + "bei Weiterberechnung an die Gemeinde aber erforderlich.",
        "Zahlungsbedingung als Freitext statt als strukturiertes Feld angegeben.",
      ],
      original: DOKUMENT.rechnungKabelwerk,
      originalHash: "sha256:9b3d7e01a4c85f2260de13ab7c94f5081e2a6b3d47f90cc21584e7a6bd309fe2",
    },
    {
      ref: DOKUMENT.rechnungFehlerhaft,
      eingangsvorgang: EINGANG.rechnungFehlerhaft,
      steller: "Leuchtenhandel Ost e.K.",
      rechnungsnummer: "LH-2026-0912",
      datum: tag(bezugstag, -2),
      // Der Betrag aus dem XML. Welcher der richtige ist, entscheidet die
      // Bürokraft — nicht der Parser, und schon gar nicht stillschweigend.
      betragCent: 422100,
      waehrung: "EUR",
      profil: "zugferd-basic",
      validierung: "ungueltig",
      befunde: [
        "Gesamtbetrag im eingebetteten XML (4.221,00 EUR) weicht vom sichtbaren PDF "
        + "(4.212,00 EUR) ab.",
        "BR-CO-15: Summe der Positionen ergibt 4.212,00 EUR und stützt damit das PDF.",
        "Rechnung wurde nicht zugeordnet und nicht zur Zahlung vorgemerkt.",
      ],
      original: DOKUMENT.rechnungFehlerhaft,
      originalHash: "sha256:c07a19be48d3f5216ba90e7cd4183f62a705be91cc38d40f7a2e6b915d08c374",
    },
  ];
}
